import {
  timeToMinutes,
  minutesToTime,
  parseYearsMonthsToDecimal,
  formatDecimalToYearsMonths,
  parseIntegerNumericValue,
  parseCyclesValue,
  isDecimalNumericTsrUnit
} from './componentFormUtils';

export const parseValueByUnit = (valueStr, unit) => {
  if (!valueStr || !unit) return 0;
  const trimmedUnit = String(unit).trim();
  const trimmedValue = String(valueStr).trim();

  if (trimmedUnit === 'Hours') {
    // Accept both HH:MM and decimal hours (e.g. "2500.5").
    // Internally, comparison math is done in minutes for Hours.
    if (trimmedValue.includes(':')) {
      return timeToMinutes(trimmedValue);
    }

    const decimalHours = parseFloat(trimmedValue.replace(',', '.'));
    if (Number.isFinite(decimalHours)) {
      return Math.round(decimalHours * 60);
    }

    // If it isn't parseable as a decimal, treat it as 0.
    return 0;
  }
  if (trimmedUnit === 'Years') {
    return parseYearsMonthsToDecimal(trimmedValue);
  }
  if (trimmedUnit === 'Calendar Days' || trimmedUnit === 'Landings') {
    return parseIntegerNumericValue(trimmedValue);
  }
  if (isDecimalNumericTsrUnit(trimmedUnit)) {
    return parseCyclesValue(trimmedValue);
  }

  const parsed = parseFloat(trimmedValue);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const formatValueByUnit = (numericValue, unit) => {
  if (numericValue == null || Number.isNaN(numericValue)) return '';
  const trimmedUnit = String(unit || '').trim();

  if (trimmedUnit === 'Hours') {
    return minutesToTime(Math.max(0, Math.floor(numericValue)));
  }
  if (trimmedUnit === 'Years') {
    return formatDecimalToYearsMonths(Math.max(0, numericValue));
  }
  if (trimmedUnit === 'Calendar Days' || trimmedUnit === 'Landings') {
    return Math.max(0, Math.floor(numericValue)).toString();
  }
  if (isDecimalNumericTsrUnit(trimmedUnit)) {
    return (Math.round(Math.max(0, numericValue) * 100) / 100).toString();
  }

  return String(numericValue);
};

export const findAirframeAssembly = (assemblies) => {
  if (!assemblies || !Array.isArray(assemblies)) return { assembly: null, error: 'No assemblies provided.' };
  const airframes = assemblies.filter((a) => (a?.ataChapterId?.name || '').trim() === 'Airframe');
  if (airframes.length === 0) return { assembly: null, error: 'No Airframe assembly (ATA Chapter Name "Airframe") found for this aircraft.' };
  if (airframes.length > 1) return { assembly: null, error: 'Multiple Airframe assemblies (ATA Chapter Name "Airframe") found; cannot choose arbitrarily.' };
  return { assembly: airframes[0], error: null };
};

export const findMatchingAssemblyTSR = (componentRecord, assemblyTimeSinceRecords) => {
  if (!componentRecord || !assemblyTimeSinceRecords || !Array.isArray(assemblyTimeSinceRecords)) {
    return null;
  }

  const type = componentRecord.type;
  const label = (componentRecord.label || '').trim();
  const unit = componentRecord.elapsedTime?.unit || componentRecord.limit?.unit || componentRecord.remainingTime?.unit;
  if (!type || !unit || !label) return null;

  const normalizedLabel = label.toLowerCase();
  const normalizedUnit = String(unit).trim();

  return assemblyTimeSinceRecords.find((a) => {
    if (!a) return false;
    const aType = a.type;
    const aLabel = (a.label || '').trim().toLowerCase();
    const aUnit = a.elapsedTime?.unit || a.limit?.unit || a.remainingTime?.unit;
    if (!aType || !aUnit || !aLabel) return false;
    return (
      aType === type &&
      aLabel === normalizedLabel &&
      String(aUnit).trim() === normalizedUnit
    );
  }) || null;
};

export const computeAssemblyComparison = (componentRecord, assemblyTimeSinceRecords) => {
  const match = findMatchingAssemblyTSR(componentRecord, assemblyTimeSinceRecords);
  if (!match) {
    return {
      installedAtAssembly: null,
      dueAtAssembly: null,
      message: 'No matching Assembly Time Since Record (same type, unit and label).',
      endDate: null
    };
  }

  const unit = match.elapsedTime?.unit || match.limit?.unit || match.remainingTime?.unit;
  const assemblyElapsedStr = match.elapsedTime?.value;
  const componentElapsedStr = componentRecord.elapsedTime?.value;
  const componentRemainingStr = componentRecord.remainingTime?.value;

  if (!unit || !assemblyElapsedStr || !componentElapsedStr || !componentRemainingStr) {
    return {
      installedAtAssembly: null,
      dueAtAssembly: null,
      message: 'Insufficient data to calculate Assembly comparison.',
      endDate: null
    };
  }

  try {
    const assemblyElapsed = parseValueByUnit(assemblyElapsedStr, unit);
    const componentElapsed = parseValueByUnit(componentElapsedStr, unit);
    const componentRemaining = parseValueByUnit(componentRemainingStr, unit);

    const installedNumeric = Math.max(0, assemblyElapsed - componentElapsed);
    const dueNumeric = assemblyElapsed + componentRemaining;

    const installedAtAssembly = formatValueByUnit(installedNumeric, unit);
    const dueAtAssembly = formatValueByUnit(dueNumeric, unit);

    return {
      installedAtAssembly,
      dueAtAssembly,
      message: null,
      endDate: null
    };
  } catch (e) {
    return {
      installedAtAssembly: null,
      dueAtAssembly: null,
      message: 'Unable to calculate Assembly comparison.',
      endDate: null
    };
  }
};

export const computeComponentSelfComparison = (componentRecord) => {
  if (!componentRecord) {
    return {
      installedAtComponent: null,
      dueAtComponent: null
    };
  }

  const unit =
    componentRecord.elapsedTime?.unit ||
    componentRecord.limit?.unit ||
    componentRecord.remainingTime?.unit;
  const limitUnit = componentRecord.limit?.unit;
  if (!unit) {
    return {
      installedAtComponent: null,
      dueAtComponent: null
    };
  }

  // For Component self-comparison, always base "Installed At" on the
  // "Elapsed value of THIS Component itself at Installation" field
  // (i.e., the elapsedTime snapshot captured in this form), so it
  // cannot be influenced by other value-at-installation fields.
  const installedValueStr = componentRecord.elapsedTime?.value;

  let installedAtComponent = null;
  if (installedValueStr != null && String(installedValueStr).trim() !== '') {
    try {
      const installedNumeric = parseValueByUnit(installedValueStr, unit);
      installedAtComponent = formatValueByUnit(installedNumeric, unit);
    } catch (e) {
      installedAtComponent = null;
    }
  }

  const limitStr = componentRecord.limit?.value;
  const remainingStr = componentRecord.remainingTime?.value;
  const elapsedStr = componentRecord.elapsedTime?.value;

  let dueAtComponent = null;

  try {
    if (limitStr != null && String(limitStr).trim() !== '') {
      // When Life Limit exists, Due At THIS should mirror that limit in the limit's own unit.
      // This avoids mixed-unit rows (e.g. limit=Years, elapsed=Calendar Days) being misformatted.
      const dueUnit = limitUnit || unit;
      const limitNumeric = parseValueByUnit(limitStr, dueUnit);
      dueAtComponent = formatValueByUnit(limitNumeric, dueUnit);
    } else if (
      remainingStr != null &&
      String(remainingStr).trim() !== '' &&
      installedValueStr != null &&
      String(installedValueStr).trim() !== ''
    ) {
      const remainingNumeric = parseValueByUnit(remainingStr, unit);
      const installedNumeric = parseValueByUnit(installedValueStr, unit);
      const dueNumeric = installedNumeric + remainingNumeric;
      dueAtComponent = formatValueByUnit(dueNumeric, unit);
    }
  } catch (e) {
    dueAtComponent = null;
  }

  return {
    installedAtComponent,
    dueAtComponent
  };
};

/**
 * Assembly self-comparison: Installed At uses assemblySelfValueAtInstallation (or installedValue fallback), then elapsedTime. Due same as component.
 */
export const computeAssemblySelfComparison = (record) => {
  if (!record) {
    return { installedAtComponent: null, dueAtComponent: null };
  }

  const unit =
    record.elapsedTime?.unit ||
    record.limit?.unit ||
    record.remainingTime?.unit;
  const limitUnit = record.limit?.unit;
  if (!unit) {
    return { installedAtComponent: null, dueAtComponent: null };
  }

  const installedSource =
    record.assemblySelfValueAtInstallation?.value != null && String(record.assemblySelfValueAtInstallation.value).trim() !== ''
      ? record.assemblySelfValueAtInstallation.value
      : (record.installedValue?.value != null && String(record.installedValue.value).trim() !== ''
          ? record.installedValue.value
          : record.elapsedTime?.value);

  let installedAtComponent = null;
  if (installedSource != null && String(installedSource).trim() !== '') {
    try {
      const installedNumeric = parseValueByUnit(installedSource, unit);
      installedAtComponent = formatValueByUnit(installedNumeric, unit);
    } catch (e) {
      installedAtComponent = null;
    }
  }

  const limitStr = record.limit?.value;
  const remainingStr = record.remainingTime?.value;
  const elapsedStr = record.elapsedTime?.value;
  let dueAtComponent = null;
  try {
    if (limitStr != null && String(limitStr).trim() !== '') {
      // Keep displayed due aligned with Life Limit unit for mixed-unit rows.
      const dueUnit = limitUnit || unit;
      dueAtComponent = formatValueByUnit(parseValueByUnit(limitStr, dueUnit), dueUnit);
    } else if (
      remainingStr != null && String(remainingStr).trim() !== '' &&
      elapsedStr != null && String(elapsedStr).trim() !== ''
    ) {
      const dueNumeric = parseValueByUnit(elapsedStr, unit) + parseValueByUnit(remainingStr, unit);
      dueAtComponent = formatValueByUnit(dueNumeric, unit);
    }
  } catch (e) {
    dueAtComponent = null;
  }

  return {
    installedAtComponent,
    dueAtComponent
  };
};

